import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { clean } from '@/lib/utils';
import type { IObject } from '@/domains/core/core.types';

import { fetchEvaluations } from '../evaluationApi';
import { normalizeEvaluationsListPage } from '../selectors/evaluation.selectors';

interface UseEvaluationsInfiniteOptions {
  bitId?: string;
  selectedEnvironment: string;
}

export const getEvaluationsInfiniteQueryKey = ({
  bitId,
  selectedEnvironment,
}: UseEvaluationsInfiniteOptions) =>
  ['evaluations', { bitId, selectedEnvironment }] as const;

export const useEvaluationsInfinite = ({
  bitId,
  selectedEnvironment,
}: UseEvaluationsInfiniteOptions) => {
  const filters = useMemo(
    () => ({
      bitId,
      environments: [selectedEnvironment],
      latestOnly: false,
      includeCount: true,
      limit: 100,
    }),
    [bitId, selectedEnvironment],
  );

  return useInfiniteQuery({
    queryKey: getEvaluationsInfiniteQueryKey({
      bitId,
      selectedEnvironment,
    }),
    queryFn: async ({ pageParam }) => {
      const response = await fetchEvaluations(
        clean({
          ...filters,
          continuationToken: pageParam,
        }),
      );

      return normalizeEvaluationsListPage(response, {
        excludeEmpty: true,
      });
    },
    enabled: !!bitId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    getNextPageParam: (lastPage) => lastPage.data.continuationToken,
    initialPageParam: '',
  });
};

interface UseEvaluationsListOptions {
  filters: IObject;
  enabled?: boolean;
}

export const getEvaluationsListQueryKey = (filters: IObject) =>
  ['evaluations', filters] as const;

export const useEvaluationsList = ({
  filters,
  enabled = true,
}: UseEvaluationsListOptions) => {
  const queryFilters = useMemo(() => clean(filters), [filters]);

  return useQuery({
    queryKey: getEvaluationsListQueryKey(queryFilters),
    queryFn: async () => {
      const response = await fetchEvaluations(queryFilters);
      return normalizeEvaluationsListPage(response);
    },
    enabled,
  });
};
